import axios from 'axios';
import { ApartmentCompare, ApartmentTransactionItem } from '../type/ApartmentCompare.type';
import { useApartmentCompareStore } from '../store/ApartmentCompare.store';

// .env.development.local 에 REACT_APP_SEOUL_API_KEY=인증키 설정
// 서울 열린데이터광장(data.seoul.go.kr)에서 발급받은 인증키 사용
// 서비스명: 서울시 부동산 실거래가 정보 (tbLnOpendataRtmsV)
const SEOUL_API_KEY = process.env.REACT_APP_SEOUL_API_KEY || '';
const SERVICE = 'tbLnOpendataRtmsV';

// package.json proxy: http://openapi.seoul.go.kr:8088
// 상대경로로 호출 → 개발서버가 openapi.seoul.go.kr:8088 로 프록시
const buildUrl = (start: number, end: number, year: string, cgCode: string) =>
  `/${SEOUL_API_KEY}/json/${SERVICE}/${start}/${end}/${year}/${cgCode}/`;

type SeoulRow = {
  BLDG_NM: string;
  BLDG_USG: string;
  ARCH_AREA: string;
  CTRT_DAY: string; // YYYYMMDD
  THING_AMT: string; // 만원
};

const fetchAllRows = async (year: string, cgCode: string): Promise<SeoulRow[]> => {
  const rows: SeoulRow[] = [];
  let start = 1;

  while (true) {
    const end = start + 999;
    const response = await axios.get(buildUrl(start, end, year, cgCode));
    const result = response.data?.[SERVICE];

    if (!result || result.RESULT?.CODE !== 'INFO-000') break;

    const batch: SeoulRow[] = Array.isArray(result.row) ? result.row : result.row ? [result.row] : [];
    rows.push(...batch);

    if (end >= result.list_total_count) break;
    start = end + 1;
  }

  return rows;
};

export const useApartmentCompareAPI = () => {
  const {
    insertApartmentCompareToStore,
    getApartmentCompareListFromStore,
    updateApartmentCompareInStore,
    deleteApartmentCompareAtStore,
  } = useApartmentCompareStore();

  const insertApartmentCompare = async (apartmentCompare: ApartmentCompare) => {
    return insertApartmentCompareToStore(apartmentCompare);
  };

  const getApartmentCompareList = async () => {
    return getApartmentCompareListFromStore();
  };

  const updateApartmentCompare = async (apartmentCompare: ApartmentCompare) => {
    return updateApartmentCompareInStore(apartmentCompare);
  };

  const deleteApartmentCompare = async (id: string) => {
    return deleteApartmentCompareAtStore(id);
  };

  // 특정 아파트 실거래 내역 조회 (자산 데이터 기간 전체)
  const fetchApartmentTransactions = async (
    cgCode: string,
    aptName: string,
    archArea: number,
    yearMonthList: string[],
  ): Promise<ApartmentTransactionItem[]> => {
    const assetYears = Array.from(new Set(yearMonthList.map((ym) => ym.substring(0, 4)))).sort();
    const extraYear = String(parseInt(assetYears[0], 10) - 1);
    const years = [extraYear, ...assetYears];

    const byMonthSum = new Map<string, number>();
    const byMonthCount = new Map<string, number>();

    await Promise.all(
      years.map(async (year) => {
        try {
          const rows = await fetchAllRows(year, cgCode);
          rows.forEach((row) => {
            const name = row.BLDG_NM?.trim();
            const usage = row.BLDG_USG?.trim();
            const area = parseFloat(row.ARCH_AREA);
            if (usage !== '아파트' || name !== aptName || Math.abs(area - archArea) > 0.5) return;

            const ctrt = row.CTRT_DAY || '';
            const ym = `${ctrt.substring(0, 4)}-${ctrt.substring(4, 6)}`;
            const price = parseInt(row.THING_AMT?.toString().replace(/,/g, ''), 10);

            if (!isNaN(price)) {
              byMonthSum.set(ym, (byMonthSum.get(ym) ?? 0) + price);
              byMonthCount.set(ym, (byMonthCount.get(ym) ?? 0) + 1);
            }
          });
        } catch {
          // 해당 연도 데이터 없음 - 무시
        }
      }),
    );

    return Array.from(byMonthSum.entries())
      .map(([yearMonth, sum]) => ({ yearMonth, price: Math.round(sum / byMonthCount.get(yearMonth)!) }))
      .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
  };

  // 아파트 이름 키워드로 검색
  const searchApartments = async (
    cgCode: string,
    aptNameKeyword: string,
    searchYear: string,
  ): Promise<{ aptName: string; excluUseAr: number }[]> => {
    const found = new Map<string, { aptName: string; excluUseAr: number }>();

    const rows = await fetchAllRows(searchYear, cgCode);
    rows.forEach((row) => {
      const name = row.BLDG_NM?.trim() || '';
      const usage = row.BLDG_USG?.trim();
      const area = parseFloat(row.ARCH_AREA);

      if (usage === '아파트' && name.includes(aptNameKeyword) && !isNaN(area)) {
        const key = `${name}|${area}`;
        if (!found.has(key)) {
          found.set(key, { aptName: name, excluUseAr: area });
        }
      }
    });

    return Array.from(found.values()).sort((a, b) => a.aptName.localeCompare(b.aptName));
  };

  return {
    insertApartmentCompare,
    getApartmentCompareList,
    updateApartmentCompare,
    deleteApartmentCompare,
    fetchApartmentTransactions,
    searchApartments,
  };
};
